import { createSelector } from '@ngrx/store';
import { State } from 'reducers';
import { ROLE } from 'constants/role.constant';

export const selectAuthentication = (state: State) => state.authentication;

export const selectIsLoginLoading = createSelector(selectAuthentication, authenticationState => authenticationState.isLoginLoading);
export const selectIsLoginError = createSelector(selectAuthentication, authenticationState => authenticationState.isLoginError);

export const selectTokenDto = createSelector(selectAuthentication, authenticationState => authenticationState.tokenDto);

export const selectIsForgotPasswordLoading = createSelector(selectAuthentication, authenticationState => authenticationState.isForgotPasswordLoading);
export const selectIsForgotPasswordSuccess = createSelector(selectAuthentication, authenticationState => authenticationState.isForgotPasswordSuccess);

export const selectUserAuthenticated = createSelector(selectAuthentication, authenticationState => authenticationState.userAuthenticated);

// BRN-2208: the session is signed in but this browser's token carries no usable BRAIN enrichment
// claims. A LOCAL HINT only (it derives from ID-token claims, while the backends read the ACCESS
// token -- L3 SEC-11); the authoritative signal is a backend `unenriched_token` 401.
export const selectIsSessionUnenriched = createSelector(selectAuthentication, authenticationState => authenticationState.isSessionUnenriched);

// BRN-2208 L5 finding F1 -- the two login failures that are NOT credential verdicts. Kept as
// separate slices from `isLoginError` on purpose: the defect being fixed was exactly one boolean
// standing in for every possible failure, so folding these back into it would restore it.
export const selectIsAccountNotProvisioned = createSelector(selectAuthentication, authenticationState => authenticationState.isAccountNotProvisioned);
export const selectIsLoginUnavailable = createSelector(selectAuthentication, authenticationState => authenticationState.isLoginUnavailable);

// BRN-2208 L6 L4-R -- the third non-credential refusal: an archived or blocked account. Its own
// slice for the same reason the two above have one, and it is not folded into
// `isAccountNotProvisioned`: that flag also drives the unenriched PAGE, which is not this user's
// situation.
export const selectIsAccountStatusRefused = createSelector(selectAuthentication, authenticationState => authenticationState.isAccountStatusRefused);

export const selectIsUserAdmin = createSelector(selectUserAuthenticated, userAuthenticated => {
  if (!userAuthenticated?.roles) {
    return false;
  }
  const roles = userAuthenticated.roles.map((role: string) => role.toUpperCase());
  return roles.includes(ROLE.admin.toUpperCase()) || roles.includes(ROLE.superadmin.toUpperCase());
});

export const selectIsUserSuperAdmin = createSelector(selectUserAuthenticated, userAuthenticated => {
  if (!userAuthenticated?.roles) {
    return false;
  }
  const roles = userAuthenticated.roles.map((role: string) => role.toUpperCase());
  return roles.includes(ROLE.superadmin.toUpperCase());
});

export const selectIsTokensLoading = createSelector(selectAuthentication, authenticationState => authenticationState.isTokensLoading);
export const selectIsTokensError = createSelector(selectAuthentication, authenticationState => authenticationState.isTokensError);
export const selectTokensResponse = createSelector(selectAuthentication, authenticationState => authenticationState.tokensResponse);

export const selectIsOtpLoading = createSelector(selectAuthentication, authenticationState => authenticationState.isOtpLoading);
export const selectOtpError = createSelector(selectAuthentication, authenticationState => authenticationState.otpError);

// BRN-2148: SSO handoff-redemption slice (the callback landing screen). There is deliberately no
// discovery/"available methods" slice — the login screen renders both SSO buttons unconditionally.
export const selectIsSsoRedeemLoading = createSelector(selectAuthentication, authenticationState => authenticationState.isSsoRedeemLoading);
export const selectIsSsoRedeemError = createSelector(selectAuthentication, authenticationState => authenticationState.isSsoRedeemError);
